import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import request, { TOKEN_KEY } from "@/utils/request";
import {
  AUTH_REFRESH_REQUESTED_EVENT,
  AUTH_SESSION_CHANGED_EVENT,
  clearCurrentUser,
  extractUserRoles,
  getCurrentUser,
  storeCurrentUser,
  USER_INFO_STORAGE_KEY,
} from "@/utils/user";
import { AuthContext, type AuthContextValue, type AuthUser } from "./auth";

export type { AuthUser } from "./auth";

function buildAuthUser(raw: Record<string, any> | null): AuthUser | null {
  if (!raw) return null;

  const roles = extractUserRoles(raw.Roles ?? raw.Role ?? raw.roles);

  const studies = Array.isArray((raw as any).Studies)
    ? ((raw as any).Studies as string[])
    : Array.isArray((raw as any).studies)
      ? ((raw as any).studies as string[])
      : [];

  const sites = Array.isArray((raw as any).Sites)
    ? ((raw as any).Sites as string[])
    : Array.isArray((raw as any).sites)
      ? ((raw as any).sites as string[])
      : [];

  const askSites = Array.isArray((raw as any).AskSites)
    ? ((raw as any).AskSites as string[])
    : Array.isArray((raw as any).askSites)
      ? ((raw as any).askSites as string[])
      : [];

  return {
    id: String((raw as any).UserId ?? (raw as any).ID ?? ""),
    name: String((raw as any).Name ?? ""),
    email: String((raw as any).Email ?? ""),
    roles,
    studies,
    sites,
    askSites,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initialRawUser = getCurrentUser();
  const [user, setUser] = useState<AuthUser | null>(() => buildAuthUser(initialRawUser));
  const [loading, setLoading] = useState(() =>
    Boolean(localStorage.getItem(TOKEN_KEY) && !initialRawUser),
  );
  const refreshPromiseRef = useRef<Promise<AuthUser | null> | null>(null);

  const setUserInfo = useCallback((raw: Record<string, unknown>) => {
    storeCurrentUser(raw);
    setUser(buildAuthUser(raw));
  }, []);

  const clearUser = useCallback(() => {
    clearCurrentUser();
    setUser(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await request.post("/_account/logout");
    } catch {
      // 即使接口失败也清除本地状态
    }
    localStorage.removeItem(TOKEN_KEY);
    clearUser();
  }, [clearUser]);

  const refreshUser = useCallback(
    async (options?: { forceTokenRefresh?: boolean }): Promise<AuthUser | null> => {
      if (!localStorage.getItem(TOKEN_KEY)) {
        setUser(null);
        setLoading(false);
        return null;
      }
      if (refreshPromiseRef.current) {
        const currentResult = await refreshPromiseRef.current;
        if (!options?.forceTokenRefresh) return currentResult;
      }

      const refreshPromise = (async () => {
        setLoading(true);
        try {
          const previousUser = buildAuthUser(getCurrentUser());
          const currentResponse = await request.get<Record<string, unknown>>(
            "/_account/checkuserinfo",
            { params: { IsApi: true } },
          );
          let rawUser = currentResponse.data;
          const currentUser = buildAuthUser(rawUser);
          const identityChanged =
            getIdentitySignature(previousUser) !== getIdentitySignature(currentUser);

          if (options?.forceTokenRefresh || identityChanged) {
            const refreshResponse = await request.post<{
              access_token: string;
              user: Record<string, unknown>;
            }>("/_account/refreshidentity");
            if (refreshResponse.data.access_token) {
              localStorage.setItem(TOKEN_KEY, refreshResponse.data.access_token);
            }
            rawUser = refreshResponse.data.user;
          }

          setUserInfo(rawUser);
          return buildAuthUser(rawUser);
        } finally {
          setLoading(false);
        }
      })();

      refreshPromiseRef.current = refreshPromise;
      try {
        return await refreshPromise;
      } finally {
        refreshPromiseRef.current = null;
      }
    },
    [setUserInfo],
  );

  useEffect(() => {
    const syncFromStorage = () => setUser(buildAuthUser(getCurrentUser()));
    const handleStorage = (event: StorageEvent) => {
      if (event.key === USER_INFO_STORAGE_KEY || event.key === TOKEN_KEY || event.key === null) {
        syncFromStorage();
      }
    };
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, syncFromStorage);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, syncFromStorage);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return;

    const refreshInBackground = () => {
      void refreshUser().catch(() => {
        // 网络短暂失败时保留现有身份快照，下一次聚焦或轮询会继续尝试。
      });
    };
    const refreshWhenActive = () => {
      if (document.visibilityState === "visible") refreshInBackground();
    };
    refreshInBackground();
    window.addEventListener("focus", refreshWhenActive);
    window.addEventListener(AUTH_REFRESH_REQUESTED_EVENT, refreshInBackground);
    document.addEventListener("visibilitychange", refreshWhenActive);
    const timer = window.setInterval(refreshWhenActive, 60_000);
    return () => {
      window.removeEventListener("focus", refreshWhenActive);
      window.removeEventListener(AUTH_REFRESH_REQUESTED_EVENT, refreshInBackground);
      document.removeEventListener("visibilitychange", refreshWhenActive);
      window.clearInterval(timer);
    };
  }, [refreshUser, user?.id]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, setUserInfo, clearUser, refreshUser, logout }),
    [clearUser, loading, logout, refreshUser, setUserInfo, user],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function getIdentitySignature(user: AuthUser | null): string {
  if (!user) return "";
  return JSON.stringify({
    ...user,
    roles: [...user.roles].sort((a, b) => a.localeCompare(b)),
    studies: [...user.studies].sort((a, b) => a.localeCompare(b)),
    sites: [...user.sites].sort((a, b) => a.localeCompare(b)),
    askSites: [...user.askSites].sort((a, b) => a.localeCompare(b)),
  });
}
